/**
 * Adapter hook for listing employees with pagination and filtering.
 * Wraps useListEmployeesQuery and transforms GraphQL DTOs to EmployeeTableData for DataTable consumption.
 */
import { Dtos_V1_EmployeeDto, QueryEmployeesArgs, useListEmployeesQuery } from '@/common/infrastructure/gql/generated/schema';

import { mapEmployeeToTableData } from '../mappers/EmployeeMappers';
import { EmployeeTableData } from '../types/EmployeeTypes';

export const useListEmployees = (filters: QueryEmployeesArgs = { skip: 0, take: 10 }) => {
  const { data, loading, error, refetch } = useListEmployeesQuery({ variables: filters });

  const employees: EmployeeTableData[] = data?.employees?.items
    ? data.employees.items.map((item) => mapEmployeeToTableData(item as Dtos_V1_EmployeeDto))
    : [];
  const pageInfo = data?.employees?.pageInfo;
  const totalCount = data?.employees?.totalCount;

  return { employees, pageInfo, totalCount, loading, error, refetch };
};

// Types for filter options
export interface FilterOption {
  id: number;
  name: string;
  checked: boolean;
}

export function buildEmployeeListWhereFilter(searchTerm: string, statusFilters?: FilterOption[], roleFilters?: FilterOption[]) {
  const and: Array<Record<string, unknown>> = [];
  if (searchTerm.trim()) {
    and.push({ name: { contains: searchTerm.trim() } });
  }
  const selectedStatusIds = statusFilters?.filter((s) => s.checked)?.map((s) => s.id) || [];
  if (selectedStatusIds.length > 0) {
    and.push({ employmentStatus: { id: { in: selectedStatusIds } } });
  }
  const selectedRoleIds = roleFilters?.filter((r) => r.checked)?.map((r) => r.id) || [];
  if (selectedRoleIds.length > 0) {
    and.push({ jobRoles: { some: { id: { in: selectedRoleIds } } } });
  }
  if (and.length > 0) {
    return { and };
  }
  return {};
}
